import { useChunkedTransfer } from "./useChunkedTransfer";

import type { FileAttachment } from "shared";

// Кэш расшифрованных блобов (по URL вложения): скачиваем/расшифровываем файл
// один раз, повторные клики и ремонты компонентов (virtua пересоздаёт сообщения
// при скролле) берут готовое. Храним промисы — параллельные запросы одного файла
// сливаются. Бюджет ограничивает память; вытесняем самые старые.
const CACHE_BUDGET = 200 * 1024 * 1024;

const blobCache = new Map<string, Promise<Blob>>();
let cacheBytes = 0;

async function evictIfNeeded(): Promise<void> {
    for (const [url, promise] of blobCache) {
        if (cacheBytes <= CACHE_BUDGET) {
            return;
        }
        const blob = await promise.catch(() => null);
        blobCache.delete(url);
        if (blob) {
            cacheBytes -= blob.size;
        }
    }
}

export function useFileDownload() {
    const { download } = useChunkedTransfer();

    // Отдаёт расшифрованный Blob (инлайн-превью, сохранение) — из кэша, если уже
    // качали. Прогресс дёргается только при реальной загрузке.
    function fetchBlob(
        attachment: FileAttachment,
        onProgress?: (fraction: number) => void
    ): Promise<Blob> {
        const cached = blobCache.get(attachment.url);
        if (cached) {
            return cached;
        }
        const promise = download(attachment, onProgress).then((blob) => {
            cacheBytes += blob.size;
            void evictIfNeeded();
            return blob;
        });
        // Провал не кэшируем — следующий клик попробует заново.
        promise.catch(() => blobCache.delete(attachment.url));
        blobCache.set(attachment.url, promise);
        return promise;
    }

    // Скачивает вложение и сохраняет его как файл.
    async function downloadFile(
        attachment: FileAttachment,
        onProgress?: (fraction: number) => void
    ): Promise<void> {
        const blob = await fetchBlob(attachment, onProgress);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = attachment.name;
        a.click();
        URL.revokeObjectURL(url);
    }

    return { downloadFile, fetchBlob };
}
